import type { CaseCptLine, Laterality, ScheduledCase, SurgeonFacility, SurgeonTemplate } from '@/lib/templates/types';

export interface AuthorizationDiagnosis {
  code: string;
  description: string;
  authorizationLinked: boolean;
  reason: string;
}

export interface OfficeManagerFlag {
  id: string;
  createdAt: string;
  caseId: string;
  patientName: string;
  title: string;
  detail: string;
  severity: 'staff_hold' | 'surgeon_review' | 'rcm_review';
  affectedCodes: string[];
  source: 'pre-auth' | 'post-op';
  surgeonCanContinue: boolean;
}

export interface FinalizedAuthorizationBundle {
  id: string;
  caseId: string;
  patientName: string;
  patientDob: string | null;
  patientMrn: string | null;
  encounterId: string | null;
  payerName: string | null;
  planName: string | null;
  surgeonName: string;
  facilityName: string | null;
  scheduledDate: string | null;
  procedureName: string;
  laterality: Laterality | null;
  cptLines: CaseCptLine[];
  diagnosisCodes: AuthorizationDiagnosis[];
  requiredAttachments: string[];
  renderedPacket: string;
  officeManagerFlags: OfficeManagerFlag[];
  finalizedAt: string;
  status: 'signed' | 'staff_hold';
}

export interface PacketReconciliationIssue {
  type: 'missing_cpt' | 'missing_modifier' | 'empty_cart';
  message: string;
  code?: string;
  modifier?: string;
}

export interface PacketReconciliationResult {
  ok: boolean;
  issues: PacketReconciliationIssue[];
}

const AUTH_BUNDLE_KEY = 'link.finalizedAuthorizationBundles.v1';
const OFFICE_MANAGER_FLAGS_KEY = 'link.officeManagerFlags.v1';

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function safeReadJson<T>(key: string, fallback: T): T {
  if (!canUseStorage()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function safeWriteJson(key: string, value: unknown): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Demo/local persistence should never break the surgeon workflow.
  }
}

export function cptCode(line: Pick<CaseCptLine, 'code'>): string {
  return String(line.code);
}

function normalizeCode(code: string | number | null | undefined): string {
  return String(code || '').trim().toUpperCase();
}

function normalizeText(text: string | null | undefined): string {
  return (text || '').toLowerCase();
}

function lateralityModifier(laterality: Laterality | null): string | null {
  if (laterality === 'left') return 'LT';
  if (laterality === 'right') return 'RT';
  if (laterality === 'bilateral') return '50';
  return null;
}

export function normalizeCptLines(cptLines: CaseCptLine[], laterality: Laterality | null): CaseCptLine[] {
  const primaryModifier = lateralityModifier(laterality);
  const seen = new Set<string>();

  return cptLines
    .map((line, index): CaseCptLine => {
      const code = normalizeCode(line.code);
      const modifiers = line.modifiers
        .map(mod => normalizeCode(mod))
        .filter(Boolean);
      const finalModifiers = line.section === 'primary' && primaryModifier && !modifiers.includes(primaryModifier)
        ? [primaryModifier, ...modifiers]
        : modifiers;

      return {
        ...line,
        code,
        modifiers: Array.from(new Set(finalModifiers)),
        section: index === 0 && !cptLines.some(item => item.section === 'primary') ? 'primary' : line.section,
      };
    })
    .filter((line) => {
      const code = cptCode(line);
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
}

function hasAnyCode(codes: Set<string>, candidates: string[]): boolean {
  return candidates.some(code => codes.has(code));
}

function diagnosisMatchesActiveCpts(diagnosis: { code: string; description: string }, activeCodes: Set<string>): { linked: boolean; reason: string } {
  const code = normalizeCode(diagnosis.code);
  const description = normalizeText(diagnosis.description);

  if (/^M75\.2/.test(code) || /\b(biceps|bicipital)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['23430', '29828']),
      reason: 'Biceps diagnosis maps to biceps tenodesis CPTs 23430 or 29828.',
    };
  }

  if (/^S43\.43/.test(code) || /\b(slap|labrum|labral|glenoid labrum)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['29807', '29806']),
      reason: 'Labral diagnosis maps to the selected shoulder labral repair CPT.',
    };
  }

  if (/^M75\.1/.test(code) || /\b(rotator cuff|supraspinatus|infraspinatus)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['23420', '23410', '23412', '29827']),
      reason: 'Rotator cuff diagnosis maps to the selected cuff repair CPT.',
    };
  }

  if (/^M19\.01/.test(code) || /\b(acromioclavicular|ac joint|distal clavicle)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['23120', '29824']),
      reason: 'AC joint diagnosis maps to distal clavicle excision CPTs 23120 or 29824.',
    };
  }

  if (/^M25\.66/.test(code) || /\b(arthrofibrosis|stiffness)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['27570']),
      reason: 'Knee stiffness diagnosis maps to knee manipulation CPT 27570.',
    };
  }

  if (/^S76\.11/.test(code) || /\b(extensor mechanism|quadriceps|vmo)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['27385', '27422']),
      reason: 'Extensor mechanism diagnosis maps to extensor repair CPTs.',
    };
  }

  if (/^G56\.0/.test(code) || /\b(carpal tunnel|median neuropathy)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['64721']),
      reason: 'Carpal tunnel diagnosis maps to median neuroplasty CPT 64721.',
    };
  }

  if (/^M17\./.test(code) || /\b(osteoarthritis|bone-on-bone|tricompartmental)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['27447']),
      reason: 'Osteoarthritis diagnosis maps to total knee arthroplasty CPT 27447.',
    };
  }

  if (/^M24\.56/.test(code) || /\b(contracture|capsular release|ligament balancing)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['27435', '27447']),
      reason: 'Contracture diagnosis maps to knee arthroplasty or release CPTs.',
    };
  }

  if (/^S83\./.test(code) || /^M22\./.test(code) || /\b(meniscus|meniscal|chondromalacia)\b/.test(description)) {
    return {
      linked: hasAnyCode(activeCodes, ['29880', '29881']),
      reason: 'Meniscus/chondral diagnosis maps to knee arthroscopy CPTs.',
    };
  }

  return {
    linked: true,
    reason: 'Diagnosis retained because no narrow CPT-specific exclusion rule applies.',
  };
}

export function alignDiagnosesToAuthorizedCpts(
  diagnoses: Array<{ code: string; description: string }>,
  cptLines: CaseCptLine[],
): AuthorizationDiagnosis[] {
  const activeCodes = new Set(cptLines.map(line => normalizeCode(line.code)));

  return diagnoses
    .map((diagnosis): AuthorizationDiagnosis => {
      const match = diagnosisMatchesActiveCpts(diagnosis, activeCodes);
      return {
        code: normalizeCode(diagnosis.code),
        description: diagnosis.description || normalizeCode(diagnosis.code),
        authorizationLinked: match.linked,
        reason: match.reason,
      };
    })
    .filter(diagnosis => Boolean(diagnosis.code));
}

export function authorizationLinkedDiagnoses(
  diagnoses: Array<{ code: string; description: string }>,
  cptLines: CaseCptLine[],
): AuthorizationDiagnosis[] {
  return alignDiagnosesToAuthorizedCpts(diagnoses, cptLines)
    .filter(diagnosis => diagnosis.authorizationLinked);
}

export function formatCptLineForPacket(line: CaseCptLine): string {
  const suffix = line.modifiers.length ? `-${line.modifiers.join('-')}` : '';
  return `${line.description || 'Requested procedure'} (CPT ${cptCode(line)}${suffix})`;
}

export function buildRequestedProcedureSection(cptLines: CaseCptLine[]): string {
  if (cptLines.length === 0) {
    return '1. [No CPT selected - office manager review required]';
  }

  return cptLines
    .map((line, index) => `${index + 1}. ${formatCptLineForPacket(line)}`)
    .join('\n');
}

export function validatePacketMatchesCodeCart(
  renderedPacket: string,
  cptLines: CaseCptLine[],
): PacketReconciliationResult {
  const issues: PacketReconciliationIssue[] = [];
  const text = renderedPacket || '';

  if (cptLines.length === 0) {
    issues.push({
      type: 'empty_cart',
      message: 'The Code Cart is empty. A payer packet cannot be signed without at least one CPT.',
    });
  }

  for (const line of cptLines) {
    const code = cptCode(line);
    if (!new RegExp(`\\b${code}\\b`).test(text)) {
      issues.push({
        type: 'missing_cpt',
        code,
        message: `CPT ${code} is in the Code Cart but missing from the rendered authorization packet.`,
      });
    }

    for (const modifier of line.modifiers) {
      const modPattern = new RegExp(`\\b${code}\\s*-\\s*${modifier}\\b|\\b${modifier}\\s+modifier\\b|\\bmodifier\\s+${modifier}\\b`, 'i');
      if (!modPattern.test(text)) {
        issues.push({
          type: 'missing_modifier',
          code,
          modifier,
          message: `Modifier ${modifier} for CPT ${code} is in the Code Cart but missing from the rendered authorization packet.`,
        });
      }
    }
  }

  return {
    ok: issues.length === 0,
    issues,
  };
}

export function createOfficeManagerFlag(params: {
  caseId: string;
  patientName: string;
  title: string;
  detail: string;
  affectedCodes?: string[];
  source: OfficeManagerFlag['source'];
  severity?: OfficeManagerFlag['severity'];
}): OfficeManagerFlag {
  return {
    id: `flag-${params.caseId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    caseId: params.caseId,
    patientName: params.patientName,
    title: params.title,
    detail: params.detail,
    severity: params.severity || 'staff_hold',
    affectedCodes: params.affectedCodes || [],
    source: params.source,
    surgeonCanContinue: true,
  };
}

export function saveOfficeManagerFlag(flag: OfficeManagerFlag): void {
  const flags = safeReadJson<OfficeManagerFlag[]>(OFFICE_MANAGER_FLAGS_KEY, []);
  safeWriteJson(OFFICE_MANAGER_FLAGS_KEY, [flag, ...flags].slice(0, 250));
}

export function getOfficeManagerFlags(): OfficeManagerFlag[] {
  return safeReadJson<OfficeManagerFlag[]>(OFFICE_MANAGER_FLAGS_KEY, []);
}

export function deleteOfficeManagerFlagsForCase(caseId: string | null | undefined): void {
  if (!caseId) return;
  const flags = safeReadJson<OfficeManagerFlag[]>(OFFICE_MANAGER_FLAGS_KEY, []);
  safeWriteJson(
    OFFICE_MANAGER_FLAGS_KEY,
    flags.filter(flag => flag.caseId !== caseId),
  );
}

export function buildFinalizedAuthorizationBundle(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  facility: SurgeonFacility | null;
  surgeonName: string;
  renderedPacket: string;
  requiredAttachments: string[];
  officeManagerFlags?: OfficeManagerFlag[];
}): FinalizedAuthorizationBundle {
  const normalizedLines = normalizeCptLines(params.cptLines, params.laterality);
  const linkedDiagnoses = alignDiagnosesToAuthorizedCpts(params.template.diagnosis_codes || [], normalizedLines);

  return {
    id: `auth-bundle-${params.selectedCase.id}`,
    caseId: params.selectedCase.id,
    patientName: params.selectedCase.patient_name,
    patientDob: params.selectedCase.patient_dob,
    patientMrn: params.selectedCase.patient_mrn,
    encounterId: params.selectedCase.encounter_id,
    payerName: params.selectedCase.payer_name,
    planName: params.selectedCase.plan_name,
    surgeonName: params.surgeonName,
    facilityName: params.facility?.facility_name || params.selectedCase.facility_name,
    scheduledDate: params.selectedCase.scheduled_date,
    procedureName: params.template.template_name,
    laterality: params.laterality,
    cptLines: normalizedLines,
    diagnosisCodes: linkedDiagnoses,
    requiredAttachments: params.requiredAttachments,
    renderedPacket: params.renderedPacket,
    officeManagerFlags: params.officeManagerFlags || [],
    finalizedAt: new Date().toISOString(),
    status: params.officeManagerFlags?.length ? 'staff_hold' : 'signed',
  };
}

export function saveFinalizedAuthorizationBundle(bundle: FinalizedAuthorizationBundle): void {
  const bundles = safeReadJson<Record<string, FinalizedAuthorizationBundle>>(AUTH_BUNDLE_KEY, {});
  safeWriteJson(AUTH_BUNDLE_KEY, {
    ...bundles,
    [bundle.caseId]: bundle,
  });
}

export function getFinalizedAuthorizationBundle(caseId: string | null | undefined): FinalizedAuthorizationBundle | null {
  if (!caseId) return null;
  const bundles = safeReadJson<Record<string, FinalizedAuthorizationBundle>>(AUTH_BUNDLE_KEY, {});
  return bundles[caseId] || null;
}

export function deleteFinalizedAuthorizationBundle(caseId: string | null | undefined): void {
  if (!caseId) return;
  const bundles = safeReadJson<Record<string, FinalizedAuthorizationBundle>>(AUTH_BUNDLE_KEY, {});
  if (!bundles[caseId]) return;

  const nextBundles = { ...bundles };
  delete nextBundles[caseId];
  safeWriteJson(AUTH_BUNDLE_KEY, nextBundles);
}

export function listFinalizedAuthorizationBundles(): FinalizedAuthorizationBundle[] {
  const bundles = safeReadJson<Record<string, FinalizedAuthorizationBundle>>(AUTH_BUNDLE_KEY, {});
  return Object.values(bundles).sort((a, b) => b.finalizedAt.localeCompare(a.finalizedAt));
}
